import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import classnames from 'classnames';
import { useRef, useState } from 'react';
import { trackEvent } from 'src/lib/googleAnalytics';
import { t } from 'src/lib/player/i18n';
import {
  getCurrentEvents,
  reduceEndTime,
  reduceMin,
  reduceStartTime,
} from 'src/lib/player/selectors';
import { type TimeMode, useStore } from 'src/lib/player/store';
import { leftpad2, NBSP } from 'src/lib/player/utils';
import { onClickGenerator } from 'src/lib/player/utils/onClickGenerator';
import { useOnClickOutside } from 'usehooks-ts';
import { IconPause, IconPlay } from '../BasicComponents/Icons';
import ControlButton from './ControlButton';
import Slider from './Slider';

function SpeedButton({
  onChange,
  speed,
  replaySpeedup,
  isLive,
}: {
  onChange: (newSpeed: number) => void;
  speed: number;
  replaySpeedup: number;
  isLive: boolean;
}) {
  const timerMultipliers = [16, 8, 4, 2, 1];
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useOnClickOutside(ref, () => {
    setIsOpen(false);
  });

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      {isOpen && !isLive && (
        <div className="control-button-popup">
          {timerMultipliers.map((multiplier) => (
            <div
              key={multiplier}
              className={classnames(
                'control-button',
                multiplier === speed && 'control-button-active',
              )}
              {...onClickGenerator(() => {
                onChange(multiplier);
              })}
            >
              {multiplier * replaySpeedup}×
            </div>
          ))}
        </div>
      )}
      <ControlButton
        title={t('Play faster')}
        onClick={() => {
          setIsOpen(!isOpen && !isLive);
        }}
        className={isLive ? 'speed-button-live' : ''}
        disabled={isLive}
      >
        {isLive ? 'LIVE' : `${speed * replaySpeedup}×`}
      </ControlButton>
    </div>
  );
}

function TimeDisplay({
  startTime,
  time,
  timeMode,
}: {
  startTime: number;
  time: number;
  timeMode: TimeMode;
}): JSX.Element {
  return (
    <div className="time-display" translate="no">
      {timeMode === 'ABSOLUTE' ? formatLabelAbsolute(time) : formatLabelRelative(startTime - time)}
    </div>
  );
}

function formatLabelAbsolute(time: number) {
  const date = new Date(time);

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  const min = date.getMinutes();
  const hour = date.getHours();
  const sec = date.getSeconds();

  return `${year}-${leftpad2(month)}-${leftpad2(day)}${NBSP}${leftpad2(hour)}h${NBSP}${leftpad2(
    min,
  )}m${NBSP}${leftpad2(sec)}s`;
}

function formatLabelRelative(time: number): string {
  const sec = Math.floor(time / OneSecondInMillis);
  const min = Math.floor(sec / 60);
  const hour = Math.floor(min / 60);
  const day = Math.floor(hour / 24);
  const showDays = day > 0;

  return showDays
    ? `- ${leftpad2(day)}d${NBSP}${leftpad2(hour % 24)}h${NBSP}${leftpad2(
        min % 60,
      )}m${NBSP}${leftpad2(sec % 60)}s`
    : `- ${leftpad2(hour)}h${NBSP}${leftpad2(min % 60)}m${NBSP}${leftpad2(sec % 60)}s`;
}

export default function ControlBar({ className }: { className?: string }): JSX.Element {
  const setTimerTime = useStore((state) => state.actions.setTimerTime);
  const toggleTimer = useStore((state) => state.actions.toggleTimer);
  const setTimerSpeed = useStore((state) => state.actions.setTimerSpeed);
  const timer = useStore((state) => state.timer);
  const timeMode = useStore((state) => state.options.timeMode);
  const currentEvents = useStore((state) => getCurrentEvents(state));

  const startTime = reduceStartTime(currentEvents);
  const endTime = reduceEndTime(currentEvents);
  const replaySpeedup = reduceMin(currentEvents, (a) => a.options.replaySpeedup);

  function handleSliderChange(time: number) {
    setTimerTime(time);
  }

  function handlePlayButtonClick() {
    trackEvent('player_control', { control: 'play' });
    toggleTimer();
  }

  function handleSpeedButtonClick(newSpeed: number) {
    trackEvent('player_control', { control: 'speed' });
    setTimerSpeed(newSpeed);
  }

  if (!Number.isFinite(startTime)) {
    return <div className={className} />;
  }

  return (
    <div className={className}>
      {startTime <= Date.now() ? (
        <ControlButton
          className="play-button"
          icon={timer.isPlaying ? <IconPause /> : <IconPlay />}
          title={timer.isPlaying ? t('Pause') : t('Play')}
          onClick={handlePlayButtonClick}
        />
      ) : (
        <div style={{ width: 10 }} />
      )}
      <SpeedButton
        onChange={handleSpeedButtonClick}
        isLive={timer.isLive}
        speed={timer.speed}
        replaySpeedup={replaySpeedup}
      />
      {startTime <= Date.now() ? (
        <Slider
          key={`slider-${startTime}-${endTime}`}
          value={timer.time}
          min={startTime}
          max={endTime}
          timeMode={timeMode}
          shade={[[startTime, Math.min(endTime, Date.now())]]}
          onChange={handleSliderChange}
          onChangeEnd={() => trackEvent('timeline_scrub')}
        />
      ) : (
        <TimeDisplay time={timer.time} timeMode={timeMode} startTime={startTime} />
      )}
    </div>
  );
}
